import classNames from '../_util/classNames';
import CloseCircleFilled from '@ant-design/icons-vue/CloseCircleFilled';
import PropTypes from '../_util/vue-types';
import { cloneElement } from '../_util/vnode';
import { defineComponent } from 'vue';
import { anyType, someType, tuple } from '../_util/type';
import { hasAddon } from './util';
import { FormItemInputContext } from '../form/FormItemContext';
import { getMergedStatus, getStatusClassNames } from '../_util/statusUtils';
const ClearableInputType = ['text', 'input'];
export default defineComponent({
    compatConfig: { MODE: 3 },
    name: 'ClearableLabeledInput',
    inheritAttrs: false,
    props: {
        prefixCls: String,
        inputType: PropTypes.oneOf(tuple('text', 'input')),
        value: anyType(),
        defaultValue: anyType(),
        allowClear: someType([Boolean, Object]),
        element: anyType(),
        handleReset: Function,
        disabled: { type: Boolean, default: undefined },
        direction: { type: String },
        size: { type: String },
        suffix: anyType(),
        prefix: anyType(),
        addonBefore: anyType(),
        addonAfter: anyType(),
        readonly: { type: Boolean, default: undefined },
        focused: { type: Boolean, default: undefined },
        bordered: { type: Boolean, default: true },
        variant: {
            type: String,
            default: undefined,
        },
        triggerFocus: { type: Function },
        hidden: Boolean,
        status: String,
        hashId: String,
        affixWrapperClassName: String,
        affixWrapperStyle: Object,
    },
    setup(props, { slots, attrs }) {
        const statusContext = FormItemInputContext.useInject();
        const renderClearIcon = (prefixCls) => {
            var _a;
            const { value, disabled, readonly, handleReset, suffix = slots.suffix, allowClear } = props;
            const needClear = !disabled && !readonly && value;
            const className = `${prefixCls}-clear-icon`;
            const allowClearIcon = typeof allowClear === 'object' && allowClear ? allowClear.clearIcon : undefined;
            const customIcon = allowClearIcon !== null && allowClearIcon !== void 0 ? allowClearIcon : (_a = slots.clearIcon) === null || _a === void 0 ? void 0 : _a.call(slots);
            const iconCls = classNames({
                [`${className}-hidden`]: !needClear,
                [`${className}-has-suffix`]: !!suffix,
            }, className);
            if (customIcon) {
                return (<span onClick={handleReset} onMousedown={e => e.preventDefault()} class={iconCls} role="button">
            {customIcon}
          </span>);
            }
            return (<CloseCircleFilled onClick={handleReset} onMousedown={e => e.preventDefault()} class={iconCls} role="button"/>);
        };
        const renderTextAreaWithClearIcon = (prefixCls, element) => {
            const { value, allowClear, direction, bordered, variant, hidden, status: customStatus, addonAfter = slots.addonAfter, addonBefore = slots.addonBefore, hashId, affixWrapperClassName, affixWrapperStyle, } = props;
            const { status: contextStatus, hasFeedback } = statusContext;
            const mergedVariant = variant || (bordered === false ? 'borderless' : 'outlined');
            if (!allowClear) {
                return cloneElement(element, {
                    value,
                    disabled: props.disabled,
                });
            }
            const affixWrapperCls = classNames(`${prefixCls}-affix-wrapper`, `${prefixCls}-affix-wrapper-textarea-with-clear-btn`, getStatusClassNames(`${prefixCls}-affix-wrapper`, getMergedStatus(contextStatus, customStatus), hasFeedback), {
                [`${prefixCls}-affix-wrapper-rtl`]: direction === 'rtl',
                [`${prefixCls}-affix-wrapper-borderless`]: mergedVariant === 'borderless',
                [`${prefixCls}-affix-wrapper-filled`]: mergedVariant === 'filled',
                [`${prefixCls}-affix-wrapper-underlined`]: mergedVariant === 'underlined',
                // className will go to addon wrapper
                [`${attrs.class}`]: !hasAddon({ addonAfter, addonBefore }) && attrs.class,
            }, hashId, affixWrapperClassName);
            return (<span class={affixWrapperCls} style={Object.assign(Object.assign({}, attrs.style), affixWrapperStyle)} hidden={hidden}>
          {cloneElement(element, {
                    style: null,
                    value,
                    disabled: props.disabled,
                })}
          {renderClearIcon(prefixCls)}
        </span>);
        };
        return () => {
            var _a;
            const { prefixCls, inputType, element = (_a = slots.element) === null || _a === void 0 ? void 0 : _a.call(slots) } = props;
            if (inputType === ClearableInputType[0]) {
                return renderTextAreaWithClearIcon(prefixCls, element);
            }
            return null;
        };
    },
});
